// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { environment } from '@shared/environment';

/**
 * Auth0 cache tag types enum.
 *
 * Centralized enum for Auth0-related RTK Query cache tags. Use in providesTags and
 * invalidatesTags for type safety in injected endpoints (e.g. auth-api.ts).
 *
 * @remarks
 * This enum is used by `auth0Api` and by `authApi` (in injected endpoints) for
 * cache invalidation (e.g. invalidate userinfo when the token is refreshed).
 * @example
 *
 * ```typescript
 * providesTags: [Auth0TagTypes.Auth0Userinfo];
 * invalidatesTags: [Auth0TagTypes.Auth0Userinfo];
 * ```
 */
export enum Auth0TagTypes {
  Auth0Userinfo = 'Auth0Userinfo',
}

const auth0AwareBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const authConfig = environment.AUTH;

  return fetchBaseQuery({
    baseUrl: `https://${authConfig.DOMAIN!}`,
    headers: {
      Accept: environment.API_CONSTANTS.HEADERS.ACCEPT,
    },
  })(args, api, extraOptions);
};

/**
 * Auth0 Authentication API base.
 *
 * Base RTK Query API for the Auth0 Authentication API. Endpoints are injected in
 * {@link authApi} (auth-api.ts): token exchange, refresh, revoke, userinfo, and
 * authorize URL generation.
 *
 * @remarks
 * - **baseUrl:** `https://${environment.AUTH.DOMAIN}` (Auth0 tenant for the current
 *   hostname and bundle).
 * - **Default headers:** `Accept` from `environment.API_CONSTANTS.HEADERS.ACCEPT`.
 * - **Token/revoke:** Endpoints use `Content-Type: application/x-www-form-urlencoded`
 *   per request (set in auth-api.ts).
 * - **Userinfo:** Implemented with a custom `queryFn` that reads the access token
 *   from the auth slice and requests `GET /userinfo?access_token=...`.
 *
 * @see {@link https://auth0.com/docs/api/authentication} Auth0 Authentication API.
 */
export const auth0Api = createApi({
  baseQuery: auth0AwareBaseQuery,
  endpoints: () => ({}),
  reducerPath: 'auth0Api',
  tagTypes: [Auth0TagTypes.Auth0Userinfo],
});
